import React from 'react';
import { Sparkles, X, MapPin, Tag, Loader2 } from 'lucide-react';

const CATEGORY_MAP = {
  financieel: 'Financieel & Belasting',
  contracten: 'Contracten & Huur',
  leveranciers: 'Leveranciers & Inkoop',
  techniek: 'Techniek & Apparatuur',
  voertuigen: 'Voertuigen',
  vastgoed: 'Vastgoed & Facilitair',
  veiligheid: 'Veiligheid & Compliance',
  milieu: 'Milieu & Infrastructuur',
  nieuwbouw: 'Nieuwbouw & Verbouwing',
  overnames: 'Overnames',
  automatisering: 'Automatisering',
};

function ConfidenceBorder(confidence) {
  if (confidence === 'high') return 'border-[#2E75B6] bg-[#2E75B6]/5 dark:bg-[#2E75B6]/10';
  if (confidence === 'medium') return 'border-[#2E75B6]/50 bg-[#2E75B6]/5 dark:bg-[#2E75B6]/5';
  return 'border-dashed border-slate-300 dark:border-[#30363D] bg-slate-50 dark:bg-[#161B22]';
}

export default function AiSuggestionChips({ suggestions, loading, onApply, onDismiss, compact = false }) {
  if (loading) {
    return (
      <div data-testid="ai-suggestion-loading" className={`flex items-center gap-2 ${compact ? 'py-2' : 'py-3 px-1'}`}>
        <Loader2 className="w-3.5 h-3.5 text-[#2E75B6] animate-spin" />
        <span className="text-xs text-slate-500 dark:text-slate-400">AI denkt na...</span>
      </div>
    );
  }

  if (!suggestions) return null;

  const { category, category_confidence, location, tags } = suggestions;

  return (
    <div
      data-testid="ai-suggestion-chips"
      className={`rounded-lg border border-[#2E75B6]/20 dark:border-[#2E75B6]/30 bg-[#2E75B6]/[0.03] dark:bg-[#2E75B6]/[0.06] ${compact ? 'p-2.5' : 'p-3'}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-[#2E75B6]" />
          <span className="text-[11px] font-medium text-[#2E75B6]">AI Suggestie</span>
        </div>
        <button
          data-testid="ai-suggestion-dismiss"
          onClick={onDismiss}
          type="button"
          className="p-0.5 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {category && (
          <button
            data-testid="ai-chip-category"
            type="button"
            onClick={() => onApply('category', category)}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[11px] font-medium transition-all hover:scale-[1.02] active:scale-[0.98] cursor-pointer ${ConfidenceBorder(category_confidence)} text-slate-700 dark:text-slate-200`}
          >
            <Tag className="w-3 h-3" />
            {CATEGORY_MAP[category] || category}
          </button>
        )}

        {location && (
          <button
            data-testid="ai-chip-location"
            type="button"
            onClick={() => onApply('location', location)}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[11px] font-medium transition-all hover:scale-[1.02] active:scale-[0.98] cursor-pointer ${ConfidenceBorder(category_confidence)} text-slate-700 dark:text-slate-200`}
          >
            <MapPin className="w-3 h-3" />
            {location}
          </button>
        )}

        {tags && tags.length > 0 && tags.map((tag) => (
          <button
            data-testid={`ai-chip-tag-${tag}`}
            type="button"
            key={tag}
            onClick={() => onApply('tag', tag)}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#161B22] text-[11px] text-slate-600 dark:text-slate-300 transition-all hover:scale-[1.02] hover:border-[#2E75B6]/40 active:scale-[0.98] cursor-pointer"
          >
            #{tag}
          </button>
        ))}
      </div>
    </div>
  );
}
